import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "text-lg font-semibold text-(--text-text-black-default) dark:text-(--text-text-white-default)" };
const _hoisted_2 = { class: "flex flex-col gap-x-4" };
const _hoisted_3 = { class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)" };
const _hoisted_4 = { class: "flex flex-col gap-x-4 min-w-0" };
const _hoisted_5 = {
    key: 0,
    class: "rounded-xl border border-(--border-border-base-medium) bg-(--background-background-white-secondary-soft) p-4 text-sm text-(--text-text-black-default) whitespace-pre-line break-all"
};
import { AutoComplete, Button, Modal, SelectInput, } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { applyParsedAddress, parseAddressQuery } from "../../domain/address-utils";
import { pinSuggestionPoint, pointForFields } from "../../domain/suggestion-point";
import { useAddressFormatter } from "@/shared/composables/useAddressFormatter";
import { defaultAddressCountry, setAddressCountry, setAddressStreetLine, toAddressLayout, requiredAddressFields, } from "@/shared/utils/addressFieldConfig";
import { useWorkspaceCountry } from "@/shared/composables/useWorkspaceCountry";
import { useAddressSearch } from "../../composables/useAddressSearch";
import AddressDetailsFields from "./AddressDetailsFields.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'AddressFormModal',
    props: {
        modelValue: { type: Boolean },
        address: { default: undefined },
        canEdit: { type: Boolean, default: false }
    },
    emits: ["update:modelValue", "save"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t } = useI18n();
        const workspaceCountry = useWorkspaceCountry();
        const addressFormatter = useAddressFormatter();
        const isEdit = computed(() => !!props.address);
        const form = reactive({
            line1: "",
            line2: "",
            line3: "",
            houseNumber: "",
            houseNumberSuffix: "",
            postalCode: "",
            city: "",
            region: "",
            country: "",
            type: "Headquarters",
        });
        const layout = workspaceCountry;
        const addressQuery = ref("");
        const seededAddressQuery = ref("");
        const addressReplaced = ref(false);
        const pinnedPoint = ref(null);
        // ---------------------------------------------------------------------------
        // Address details sub-modal refs
        // ---------------------------------------------------------------------------
        // Declared before the watch below because the watch callback (immediate: true)
        // references these. Hoisting here avoids the TDZ that fires otherwise.
        const showDetailsModal = ref(false);
        const detailsTouched = reactive({});
        const requiredDetailFields = computed(() => requiredAddressFields(layout.value));
        const requiredDetailsFilled = computed(() => requiredDetailFields.value.every((field) => form[field.key].trim()));
        const detailsErrors = computed(() => {
            const errors = {};
            for (const field of requiredDetailFields.value) {
                if (detailsTouched[field.key] && !form[field.key].trim() && field.requiredMessageKey) {
                    errors[field.key] = t(field.requiredMessageKey);
                }
            }
            return errors;
        });
        function onDetailsFieldBlur(key) {
            detailsTouched[key] = true;
        }
        function resetDetailsTouched() {
            for (const key of Object.keys(detailsTouched)) {
                delete detailsTouched[key];
            }
        }
        // ---------------------------------------------------------------------------
        // Address lookup
        // ---------------------------------------------------------------------------
        // Also declared before the watch for the same TDZ reason (addressSuggestions).
        const { suggestions: addressSuggestions, loading: addressLoading, search: _onAddressSearch, } = useAddressSearch();
        function onAddressSearch(query) {
            addressQuery.value = query;
            _onAddressSearch(query);
        }
        watch(() => [props.modelValue, props.address], ([visible, address]) => {
            if (visible) {
                form.line1 = address?.line1 ?? "";
                form.line2 = address?.line2 ?? "";
                form.line3 = address?.line3 ?? "";
                form.houseNumber = address?.houseNumber ?? "";
                form.houseNumberSuffix = address?.houseNumberSuffix ?? "";
                form.postalCode = address?.postalCode ?? "";
                form.city = address?.city ?? "";
                form.region = address?.region ?? "";
                form.country = address ? (address.country ?? "") : defaultAddressCountry(workspaceCountry.value);
                form.type = address?.type ?? "Headquarters";
                Object.assign(form, toAddressLayout({ ...form }, layout.value));
                // A new address shows an empty field: the pre-filled default country alone must not
                // read as a typed address (it would unlock Save).
                addressQuery.value = address
                    ? addressFormatter.format(form)
                    : "";
                seededAddressQuery.value = addressQuery.value;
                addressReplaced.value = false;
                pinnedPoint.value = null;
                addressSuggestions.value = [];
                resetDetailsTouched();
            }
        }, { immediate: true });
        const onAddressSelect = (option) => {
            addressQuery.value = option.label;
            form.houseNumber = "";
            form.houseNumberSuffix = "";
            form.region = "";
            applyParsedAddress(option.label, form, workspaceCountry.value, option.meta?.country ?? form.country, addressFormatter.names);
            if (option.meta?.city)
                form.city = option.meta.city;
            if (option.meta?.postalCode)
                form.postalCode = option.meta.postalCode;
            addressReplaced.value = true;
            pinnedPoint.value = pinSuggestionPoint(option.meta, form);
        };
        // The sub-modal edits `form` in place: Cancel (or the close button) restores the
        // state it was opened on, so an edit it discarded never reaches the save.
        let formBeforeDetails = null;
        const openDetailsModal = () => {
            resetDetailsTouched();
            formBeforeDetails = { ...form };
            showDetailsModal.value = true;
        };
        watch(showDetailsModal, (open) => {
            if (open || !formBeforeDetails)
                return;
            Object.assign(form, formBeforeDetails);
            formBeforeDetails = null;
        });
        function onDetailsFieldUpdate(key, value) {
            if (key === "country")
                setAddressCountry(form, value, workspaceCountry.value);
            else if (key === "line1")
                setAddressStreetLine(form, value, layout.value);
            else
                form[key] = value;
        }
        const savedFields = computed(() => toAddressLayout(form, layout.value));
        const addressPreview = computed(() => addressFormatter.parts(savedFields.value).join("\n"));
        function touchRequiredDetails() {
            for (const field of requiredDetailFields.value) {
                detailsTouched[field.key] = true;
            }
        }
        const saveDetails = () => {
            touchRequiredDetails();
            if (!requiredDetailsFilled.value)
                return;
            addressQuery.value = addressFormatter.format(savedFields.value);
            addressReplaced.value = true;
            formBeforeDetails = null;
            showDetailsModal.value = false;
        };
        // ---------------------------------------------------------------------------
        const typeOptions = computed(() => [
            { value: "Billing", label: t("organisation.detail.addresses.types.Billing") },
            { value: "Shipping", label: t("organisation.detail.addresses.types.Shipping") },
            { value: "Headquarters", label: t("organisation.detail.addresses.types.Headquarters") },
            { value: "Branch", label: t("organisation.detail.addresses.types.Branch") },
        ]);
        // Mirrors `handleSave`: structured `form.line1` wins, otherwise parse the
        // autocomplete string. Used to disable the save button when no `line1` can
        // be derived (e.g. user typed only "75001 Paris").
        const effectiveLine1 = computed(() => {
            if (form.line1.trim())
                return form.line1.trim();
            return parseAddressQuery(addressQuery.value, addressFormatter.names)?.line1?.trim() ?? "";
        });
        const canSave = computed(() => addressQuery.value.trim().length > 0 && effectiveLine1.value.length > 0);
        const handleSave = () => {
            if (!props.canEdit)
                return;
            if (!canSave.value)
                return;
            const queryMatchesSeeded = addressQuery.value === seededAddressQuery.value;
            const hasStructured = !!(form.line1 || form.postalCode || form.city || form.country);
            if (!hasStructured || (!addressReplaced.value && !queryMatchesSeeded)) {
                // Free-typed text describes a NEW address, so the granular fields of the previous one go
                // first - as onAddressSelect already does for a picked suggestion.
                // CONSTRAINT: toAddressLayout folds a house number the layout cannot render into line1, so
                // one left here would land on the street the user has just typed.
                form.houseNumber = "";
                form.houseNumberSuffix = "";
                form.region = "";
                applyParsedAddress(addressQuery.value, form, workspaceCountry.value, form.country, addressFormatter.names);
            }
            // line1 alone is not enough in the NL layout: its house number is required too.
            if (!requiredDetailsFilled.value) {
                openDetailsModal();
                touchRequiredDetails();
                return;
            }
            const saved = savedFields.value;
            const point = pointForFields(pinnedPoint.value, form);
            emit("save", {
                id: props.address?.id ?? crypto.randomUUID(),
                line1: saved.line1 || undefined,
                line2: saved.line2 || undefined,
                line3: saved.line3 || undefined,
                houseNumber: saved.houseNumber || undefined,
                houseNumberSuffix: saved.houseNumberSuffix || undefined,
                postalCode: saved.postalCode || undefined,
                city: saved.city || undefined,
                region: saved.region || undefined,
                country: saved.country || undefined,
                latitude: point?.latitude,
                longitude: point?.longitude,
                type: form.type,
                isPrimary: props.address?.isPrimary ?? false,
            });
            emit("update:modelValue", false);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(Modal), {
                    "model-value": __props.modelValue,
                    size: "md",
                    "show-close-button": true,
                    "footer-position": "right",
                    "close-on-escape": !showDetailsModal.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => (_ctx.$emit('update:modelValue', $event)))
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("h2", _hoisted_1, _toDisplayString(isEdit.value
                            ? _ctx.$t("organisation.detail.addresses.formModal.titleEdit")
                            : _ctx.$t("organisation.detail.addresses.formModal.titleAdd")), 1 /* TEXT */)
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _ctx.$t('organisation.detail.addresses.formModal.cancel'),
                            onClick: _cache[2] || (_cache[2] = ($event) => (_ctx.$emit('update:modelValue', false)))
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            label: _ctx.$t('organisation.detail.addresses.formModal.save'),
                            disabled: !canSave.value || !__props.canEdit,
                            onClick: handleSave
                        }, null, 8 /* PROPS */, ["label", "disabled"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_2, [
                            _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                modelValue: addressQuery.value,
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((addressQuery).value = $event)),
                                label: _ctx.$t('organisation.detail.addresses.formModal.fullAddress'),
                                placeholder: _ctx.$t('organisation.detail.mainInformation.addressPlaceholder'),
                                options: _unref(addressSuggestions),
                                loading: _unref(addressLoading),
                                "show-add-more-details": "",
                                "add-more-details-label": _ctx.$t('organisation.detail.mainInformation.addressAddMoreDetails'),
                                "keep-input-on-edit": "",
                                "full-width": "",
                                onSearch: onAddressSearch,
                                onSelect: onAddressSelect,
                                onAddMoreDetails: openDetailsModal
                            }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "add-more-details-label"]),
                            _createVNode(_unref(SelectInput), {
                                modelValue: form.type,
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((form.type) = $event)),
                                label: _ctx.$t('organisation.detail.addresses.formModal.type'),
                                options: typeOptions.value,
                                "full-width": ""
                            }, null, 8 /* PROPS */, ["modelValue", "label", "options"])
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["model-value", "close-on-escape"]),
                _createVNode(_unref(Modal), {
                    modelValue: showDetailsModal.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((showDetailsModal).value = $event)),
                    size: "sm",
                    "show-close-button": true,
                    "footer-position": "right"
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("h2", _hoisted_3, _toDisplayString(_ctx.$t("organisation.detail.mainInformation.addressModalTitle")), 1 /* TEXT */)
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            size: "md",
                            label: _ctx.$t('form.cancel'),
                            onClick: _cache[4] || (_cache[4] = ($event) => (showDetailsModal.value = false))
                        }, null, 8 /* PROPS */, ["label"]),
                        _createVNode(_unref(Button), {
                            size: "md",
                            label: _ctx.$t('form.save'),
                            disabled: !requiredDetailsFilled.value,
                            onClick: saveDetails
                        }, null, 8 /* PROPS */, ["label", "disabled"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_4, [
                            _createVNode(AddressDetailsFields, {
                                form: form,
                                country: _unref(layout),
                                errors: detailsErrors.value,
                                onUpdate: onDetailsFieldUpdate,
                                onBlur: onDetailsFieldBlur
                            }, null, 8 /* PROPS */, ["form", "country", "errors"]),
                            (addressPreview.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(addressPreview.value), 1 /* TEXT */))
                                : _createCommentVNode("v-if", true)
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
